// vendors
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, queryCache } from 'react-query';

// lego
import { Modal } from 'lego-dist';

// components
import Dialog from 'components/dialog/Dialog';
import ClassroomDetailsList from './ClassroomDetailsList';

// react query config
import { studentQuery, classroomQuery } from '../queryConfig';

// constants
import { APP_SECTIONS_PATHS } from 'constants/routes';

// styles
import { ClassroomDetailsTab } from './styles';

// images
import babyResultImg from 'images/baby_default_avatar.png';
import DeleteClassroomSuccess from 'views/DeleteClassroomView/SuccessView';

// hooks
import { useRouter } from 'routes';
import useCurrentUser from 'hooks/useCurrentUser';
import { ActionPermission } from 'lib/specialPermissions';

interface IGeneralProps {
  data: any;
  selectedItems: any;
  onSelectItem?: Function;
}

let studentIdToDelete: any;
const { deleteStudentQuery } = studentQuery;
const { updateClassroomQuery } = classroomQuery;

const baseTrans = 'settings:CENTER_SECTION.CLASSROOM_CONFIG';

const ClassroomDetailsGeneral: React.FC<IGeneralProps> = ({ data, onSelectItem, selectedItems }) => {

  const {
    navigateTo,
    currentParamsRef: {
      current: { centerName },
    },
  } = useRouter();
  const { can } = useCurrentUser();
  const { t: translate } = useTranslation();
  const [showDeleteStudentModal, setShowDeleteStudentModal] = useState(false);
  const [removeStudent, { isSuccess: isStudentDeleted }] = useMutation(
    deleteStudentQuery.resource,
  );

  const tableColumnHeaders = [
    {
      title: translate(`${baseTrans}.STUDENT_NAME`),
      withSortButton: true,
    },
    {
      title: translate(`${baseTrans}.MAIN_CAREGIVER`),
    },
    {
      title: translate('common:EMAIL'),
    },
  ];

  const updateBabies = () => {
    queryCache.setQueryData(updateClassroomQuery.key, (classroomData: any) => {
      const newBabies = classroomData.group.babies.filter(
        (baby: any) => baby.id !== studentIdToDelete,
      );
      classroomData.group.babies = [...newBabies];
      return classroomData;
    });
  };

  const onDeleteStudent = (studentId: number) => {
    studentIdToDelete = studentId;
    setShowDeleteStudentModal(true);
  };

  const onClickStudent = (studentId: number) => {
    navigateTo(APP_SECTIONS_PATHS.STUDENTS_PROFILE, { id: studentId, centerName  });
  };

  const deleteStudent = () => {
    removeStudent(studentIdToDelete);
    setShowDeleteStudentModal(false);
  };

  const renderConfirmDeleteStudentModal = () => (
    <Dialog
      show={showDeleteStudentModal}
      onMainAction={deleteStudent}
      mainText={translate('common:DELETE')}
      cancelText={translate('common:CANCEL')}
      title={translate('common:STUDENT_PROFILE:DELETE_MODAL_TITLE')}
      onDialogClose={() => setShowDeleteStudentModal(false)}
      onCancelAction={() => setShowDeleteStudentModal(false)}
    >
      <>
        {translate('common:STUDENT_PROFILE:DELETE_MODAL_SUBTITLE')} <br />
        {translate('common:STUDENT_PROFILE:DELETE_MODAL_DESC')}
      </>
    </Dialog>
  );

  useMemo(() => {
    if (isStudentDeleted) {
      updateBabies();
    }
  }, [isStudentDeleted]);

  return (
    <ClassroomDetailsTab>
      {renderConfirmDeleteStudentModal()}
      <ClassroomDetailsList
        listData={data}
        onSelectItem={onSelectItem}
        onClickItem={onClickStudent}
        selectedItems={selectedItems}
        onDeleteItem={onDeleteStudent}
        defaultItemAvatar={babyResultImg}
        tableColumnHeaders={tableColumnHeaders}
        emptyMessage={translate(`${baseTrans}.NO_STUDENTS_ADDED`)}
        withCheckboxItems={can(ActionPermission.MOVE_STUDENTS_FROM_CLASSROOM)}
      />
      <Modal
        showCloseButton
        closeButtonColor="#ACC2D3"
        openModal={isStudentDeleted}
      >
        {() => <DeleteClassroomSuccess header="Deleted student" />}
      </Modal>
    </ClassroomDetailsTab>
  );
};

export default ClassroomDetailsGeneral;
